import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Modal, Input, InputAmount } from '@/backoffice/components/ui';
import { formatCurrency } from '@/backoffice/utils';
import {
  fetchDriverWallet,
  withdrawDriverWallet,
  type DriverWalletData,
} from '@/domains/driver/services/driver.service';
import { Wallet, ArrowUpRight, Loader2 } from 'lucide-react';

export function CommunityEarningsPage() {
  const [wallet, setWallet] = useState<DriverWalletData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawForm, setWithdrawForm] = useState({
    amount: 0,
    payer_phone: '',
    mobile_money_provider: 'orange' as 'orange' | 'mtn',
  });

  useEffect(() => {
    fetchDriverWallet()
      .then(setWallet)
      .catch(() => setWallet(null))
      .finally(() => setLoading(false));
  }, []);

  const handleWithdraw = async () => {
    if (!withdrawForm.amount || !withdrawForm.payer_phone) return;
    setWithdrawing(true);
    try {
      const updated = await withdrawDriverWallet(withdrawForm);
      setWallet(updated);
      setShowWithdraw(false);
    } finally {
      setWithdrawing(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout userType="community_driver" title="Gains">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userType="community_driver" title="Gains">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {[
          { label: "Aujourd'hui", earnings: wallet?.today_earnings ?? 0, deliveries: wallet?.today_deliveries ?? 0 },
          { label: 'Cette semaine', earnings: wallet?.week_earnings ?? 0, deliveries: wallet?.week_deliveries ?? 0 },
          { label: 'Ce mois', earnings: wallet?.month_earnings ?? 0, deliveries: wallet?.month_deliveries ?? 0 },
        ].map((period) => (
          <Card key={period.label} className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-orange-700">{period.label}</p>
              <div className="w-10 h-10 rounded-full bg-orange-200 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-orange-700" />
              </div>
            </div>
            <p className="text-3xl font-bold text-orange-800">{formatCurrency(period.earnings)}</p>
            <p className="text-sm text-orange-700 mt-1">{period.deliveries} livraisons</p>
          </Card>
        ))}
      </div>

      <Card className="mb-6">
        <div className="grid md:grid-cols-4 gap-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">Total cumulé</p>
            <p className="text-xl font-semibold text-gray-900">{formatCurrency(wallet?.total_earnings ?? 0)}</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">Solde disponible</p>
            <p className="text-xl font-semibold text-orange-600">{formatCurrency(wallet?.balance ?? 0)}</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">Total livraisons</p>
            <p className="text-xl font-semibold text-gray-900">{wallet?.total_deliveries ?? 0}</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">Note moyenne</p>
            <p className="text-xl font-semibold text-gray-900">{wallet?.average_rating ?? 0}/5</p>
          </div>
        </div>
      </Card>

      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Retirer mes gains</h3>
          <Button leftIcon={<ArrowUpRight className="w-4 h-4" />} onClick={() => setShowWithdraw(true)}>
            Effectuer un retrait
          </Button>
        </div>
        <p className="text-sm text-gray-600">
          En attente de traitement : {formatCurrency(wallet?.pending_amount ?? 0)}
        </p>
      </Card>

      <Modal isOpen={showWithdraw} onClose={() => setShowWithdraw(false)} title="Retirer mes gains">
        <div className="space-y-4">
          <InputAmount
            label="Montant (GNF)"
            value={withdrawForm.amount}
            onChange={(v) => setWithdrawForm((f) => ({ ...f, amount: v }))}
          />
          <Input
            label="Numéro Mobile Money"
            value={withdrawForm.payer_phone}
            onChange={(e) => setWithdrawForm((f) => ({ ...f, payer_phone: e.target.value }))}
            placeholder="+224620000000"
          />
          <div className="flex gap-2">
            {(['orange', 'mtn'] as const).map((provider) => (
              <Button
                key={provider}
                variant={withdrawForm.mobile_money_provider === provider ? 'primary' : 'outline'}
                onClick={() => setWithdrawForm((f) => ({ ...f, mobile_money_provider: provider }))}
              >
                {provider === 'orange' ? 'Orange Money' : 'MTN MoMo'}
              </Button>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowWithdraw(false)}>Annuler</Button>
            <Button isLoading={withdrawing} onClick={handleWithdraw}>Confirmer</Button>
          </div>
        </div>
      </Modal>
    </DashboardLayout>
  );
}
